import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import Index from './pages/Index';
import About from './pages/About';
import Contact from './pages/Contact';
import Careers from './pages/Careers';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Jobs from './pages/Jobs';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import FAQ from './pages/FAQ';
import InterviewTips from './pages/InterviewTips';
import Enterprise from './pages/Enterprise';
import Pricing from './pages/Pricing';
import Login from './pages/Login';
import Signup from './pages/Signup';
import EmployerLogin from './pages/EmployerLogin';
import Dashboard from './pages/Dashboard';
import AdminPanel from './pages/AdminPanel';
import ApplicationStatus from './pages/ApplicationStatus';
import NotFound from './pages/NotFound';
import { JobsProvider } from './contexts/JobsContext';
import CookieBanner from './components/CookieBanner';
import CookiePolicy from './pages/CookiePolicy';
import JobApplication from './pages/JobApplication';
import Assessment from './pages/Assessment';
import VideoIntro from './pages/VideoIntro';
import ProtectedCandidateRoute from './components/ProtectedCandidateRoute';
import ProtectedEmployerRoute from './components/ProtectedEmployerRoute';
import { supabase } from './integrations/supabase/client';
import { Session } from '@supabase/supabase-js';

function App() {
  const [queryClient] = useState(() => new QueryClient());
  const [isInitialized, setIsInitialized] = useState(false);
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    console.log("App initializing...");
    
    // Set up auth state change listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        console.log("Auth state changed:", event);
        setSession(newSession);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log("Initial session check:", session ? "Session found" : "No session");
      setSession(session);
      
      // Set initialized after a brief delay to allow routes to fully load
      setTimeout(() => {
        setIsInitialized(true);
      }, 200);
    }).catch(error => {
      console.error("Error getting session:", error);
      // Still set initialized even if there's an error
      setTimeout(() => {
        setIsInitialized(true);
      }, 200);
    });
    
    // Check if running in Cypress environment
    if (typeof window !== 'undefined' && window.hasOwnProperty?.('Cypress')) {
      // Mock Supabase client in Cypress tests
      // @ts-ignore
      window.supabase = {
        auth: {
          signUp: () => Promise.resolve({ data: { user: { id: 'mock-user-id' } }, error: null }),
          signInWithPassword: () => Promise.resolve({ data: { session: { access_token: 'mock-access-token' } }, error: null }),
          signOut: () => Promise.resolve({ error: null }),
          getSession: () => Promise.resolve({ data: { session: { access_token: 'mock-access-token' } }, error: null }),
        },
        from: () => ({
          select: () => ({
            eq: () => ({
              single: () => Promise.resolve({ data: {}, error: null }),
            }),
          }),
          insert: () => Promise.resolve({ data: {}, error: null }),
          update: () => Promise.resolve({ data: {}, error: null }),
          delete: () => Promise.resolve({ data: {}, error: null }),
        }),
      };
    }
    
    // Clean up subscription on unmount
    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-apple-blue"></div>
      </div>
    );
  }

  console.log("App fully initialized, rendering routes");

  return (
    <QueryClientProvider client={queryClient}>
      <JobsProvider>
        <div className="App">
          <CookieBanner />
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Index />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/jobs" element={<Jobs />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/tips" element={<InterviewTips />} />
            <Route path="/enterprise" element={<Enterprise />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/employer-login" element={<EmployerLogin />} />
            
            {/* Protected candidate routes */}
            <Route path="/application" element={
              <ProtectedCandidateRoute stepName="Job Application" requiredPreviousStep="candidateInfo">
                <JobApplication />
              </ProtectedCandidateRoute>
            } />
            <Route path="/assessment" element={
              <ProtectedCandidateRoute stepName="Assessment" requiredPreviousStep="applicationData">
                <Assessment />
              </ProtectedCandidateRoute>
            } />
            <Route path="/video-intro" element={
              <ProtectedCandidateRoute stepName="Video Introduction" requiredPreviousStep="assessmentData">
                <VideoIntro />
              </ProtectedCandidateRoute>
            } />
            <Route path="/application-status" element={
              <ProtectedCandidateRoute stepName="Application Status" requiredPreviousStep={null}>
                <ApplicationStatus />
              </ProtectedCandidateRoute>
            } />
            
            {/* Protected employer routes */}
            <Route path="/dashboard" element={
              <ProtectedEmployerRoute>
                <Dashboard />
              </ProtectedEmployerRoute>
            } />
            
            {/* Admin routes - Explicitly define with wildcard path */}
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="/admin/:tab" element={<AdminPanel />} />
            
            {/* Catch-all route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </JobsProvider>
      <Toaster />
    </QueryClientProvider>
  );
}

export default App;
